import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useBookingSystem } from '@/contexts/BookingSystemContext'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { AuthModal } from '@/components/auth/AuthModal'
import { Button } from '@/components/ui/button'
import { BookingSystemAvailability } from './BookingSystemAvailability'
import { BookingSystemCalendar } from './BookingSystemCalendar'
import ArtistBookingDashboardPage from '@/pages/ArtistBookingDashboardPage'
import BookingSystemSettingsPage from '@/pages/BookingSystemSettingsPage'
import BookingSystemClientsPage from '@/pages/BookingSystemClientsPage'
import BookingSystemClientDetailPage from '@/pages/BookingSystemClientDetailPage'

/**
 * Access matches Chats / Time4Tats: `useUserData` + `bookingSystems/{artistId}.bookingSubscription`
 * (same listener as elsewhere). Do not gate on a second interpretation from context.
 */
export function BookingSystemDashboard() {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuthState()
  const {
    loading: userDataLoading,
    isArtist,
    hasBookingSystem,
    bookingSubscription,
    bookingLoading,
  } = useUserData()
  const { error: bookingContextError } = useBookingSystem()
  const [showLoginModal, setShowLoginModal] = useState(false)

  useEffect(() => {
    if (!authLoading && !user) {
      setShowLoginModal(true)
    }
  }, [user, authLoading])

  useEffect(() => {
    if (user) {
      setShowLoginModal(false)
    }
  }, [user])

  useEffect(() => {
    if (!userDataLoading && user && !isArtist) {
      navigate('/flashes', { replace: true })
    }
  }, [user, isArtist, userDataLoading, navigate])

  if (!user && !authLoading) {
    return (
      <>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center px-4">
            <h2 className="text-2xl font-semibold mb-2">Booking System</h2>
            <p className="text-muted-foreground mb-4">
              Please log in to access the booking system
            </p>
            <Button
              onClick={() => setShowLoginModal(true)}
              className="px-6 py-2"
            >
              Log In
            </Button>
          </div>
        </div>
        <AuthModal
          isOpen={showLoginModal}
          onOpenChange={setShowLoginModal}
        />
      </>
    )
  }

  if (userDataLoading || (isArtist && bookingLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
          <p className="text-foreground">Loading booking system status...</p>
        </div>
      </div>
    )
  }

  if (user && !isArtist) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
          <p className="text-muted-foreground text-sm">Redirecting…</p>
        </div>
      </div>
    )
  }

  if (bookingContextError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center text-destructive">
          <p>{bookingContextError}</p>
        </div>
      </div>
    )
  }

  if (isArtist && !hasBookingSystem) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">
            {bookingSubscription === null
              ? "You don't have access to the booking system."
              : `Your booking system subscription is ${bookingSubscription.status}.`}
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="relative min-h-screen bg-background overflow-y-auto overflow-x-hidden pt-safe-native-top md:pt-4">
        <Routes>
          <Route path="dashboard" element={<ArtistBookingDashboardPage />} />
          <Route path="availability" element={<BookingSystemAvailability />} />
          <Route path="calendar" element={<BookingSystemCalendar />} />
          <Route path="settings" element={<BookingSystemSettingsPage />} />
          <Route path="clients" element={<BookingSystemClientsPage />} />
          <Route path="clients/:clientId" element={<BookingSystemClientDetailPage />} />
          <Route path="*" element={<Navigate to="/booking-system/dashboard" replace />} />
        </Routes>
      </div>
      <AuthModal
        isOpen={showLoginModal}
        onOpenChange={setShowLoginModal}
      />
    </>
  )
}
